import { RefObject, useCallback, useEffect, useRef } from 'react';

const SIDE_OFFSET = 8;
const ARROW_EDGE_OFFSET = 12;

type Params = {
  triggerRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  preferUpwards?: boolean;
  setIsUpwards: (value: boolean) => void;
};

const usePositioning = ({
  triggerRef,
  contentRef,
  isOpen,
  preferUpwards,
  setIsUpwards,
}: Params) => {
  const rafRef = useRef<number>(null);

  const updatePosition = useCallback(
    (retry = true) => {
      const trigger = triggerRef.current;
      const content = contentRef.current;
      if (!trigger || !content) return;

      const wrapper = content.querySelector(
        '.resolve-popup__content__content-wrapper',
      ) as HTMLDivElement | null;

      if (wrapper) wrapper.style.removeProperty('--popup-arrow-left');

      const { width: contentWidth, height: contentHeight } =
        content.getBoundingClientRect();

      if (!contentWidth || !contentHeight) {
        if (retry)
          rafRef.current = requestAnimationFrame(() => updatePosition(false));
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const triggerCenterX = triggerRect.left + triggerRect.width / 2;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - triggerRect.bottom;
      const isUpwards = preferUpwards
        ? triggerRect.top >= contentHeight + SIDE_OFFSET
        : spaceBelow < contentHeight && triggerRect.top > spaceBelow;

      setIsUpwards(isUpwards);

      const top = isUpwards
        ? triggerRect.top + window.scrollY - contentHeight
        : triggerRect.bottom + window.scrollY;

      const naturalLeft = triggerCenterX + window.scrollX - contentWidth / 2;
      const clampedLeft = Math.min(
        Math.max(naturalLeft, SIDE_OFFSET + window.scrollX),
        window.scrollX + viewportWidth - contentWidth - SIDE_OFFSET,
      );

      if (wrapper && clampedLeft !== naturalLeft) {
        const arrowOffsetPx = Math.min(
          Math.max(
            triggerCenterX - (clampedLeft - window.scrollX),
            ARROW_EDGE_OFFSET,
          ),
          contentWidth - ARROW_EDGE_OFFSET,
        );
        const arrowPercent = (arrowOffsetPx / contentWidth) * 100;
        wrapper.style.setProperty('--popup-arrow-left', `${arrowPercent}%`);
      }

      content.style.top = `${top}px`;
      content.style.left = `${clampedLeft}px`;
      content.style.visibility = 'visible';
    },
    [triggerRef, contentRef, preferUpwards, setIsUpwards],
  );

  useEffect(() => {
    if (!isOpen) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (contentRef.current) contentRef.current.style.visibility = 'hidden';
      return;
    }

    rafRef.current = requestAnimationFrame(() => updatePosition());

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, updatePosition, contentRef]);

  useEffect(() => {
    if (!isOpen) return;

    const reposition = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => updatePosition());
    };

    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    const resizeObserver = new ResizeObserver(reposition);

    if (triggerRef.current) resizeObserver.observe(triggerRef.current);
    if (contentRef.current) resizeObserver.observe(contentRef.current);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      resizeObserver.disconnect();
    };
  }, [isOpen, triggerRef, contentRef, updatePosition]);
};

export default usePositioning;
